/* eslint-disable */
import React, { useState, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { TableColumn } from 'react-data-table-component';

import Arrow from 'src/assets/arrow-left-black.svg';

import { RegularButton, SearchInput } from 'pg-ui-library';

import { SettingsButton } from 'src/components';

import { IBenefitsConfigVersion } from 'src/interfaces';
import useDebounceSearchTerm from 'src/hooks/useDebounceSearchTerm';
import { useParams } from 'react-router-dom';
import {
  Container,
  ItemContainer,
  ArrowBack,
  NavTextLink,
  ItemContainerHeader,
  PageTitle,
  HeaderLeftItems,
  HeaderRightItems,
  HeaderLeftFirstItem,
  HeaderLeftSecondItem,
  TableSettingContainer,
} from './styles';

import { BenefitsConfigsVersionTable, benefitsConfigVersionTableColumns } from '../benefits-config-version-table';
import { BenefitsConfigVersionModal } from '../benefits-config-version-modal';

interface IBenefitsConfigVersionContext {
  benefitConfigVersion: IBenefitsConfigVersion | null;
  benefitConfigVersionEditModalIsOpen: boolean;
  // eslint-disable-next-line no-unused-vars
  setSelectedBenefitsConfigVersion: (b: IBenefitsConfigVersion) => void;
  // eslint-disable-next-line no-unused-vars
  setBenefitsConfigVersionEditModalIsOpen: (b: boolean) => void;
  clearSelectedBenefitsConfigVersion: () => void;
}

const useStore = (): IBenefitsConfigVersionContext => {
  const [benefitConfigVersion, setBenefitsConfigVersion] = useState<IBenefitsConfigVersion | null>(null);
  const [benefitConfigVersionEditModalIsOpen, setBenefitsConfigVersionEditModal] = useState<boolean>(false);

  const setSelectedBenefitsConfigVersion = (b: IBenefitsConfigVersion): void => {
    setBenefitsConfigVersion(b);
  };

  const clearSelectedBenefitsConfigVersion = (): void => {
    setBenefitsConfigVersion(null);
  };

  const setBenefitsConfigVersionEditModalIsOpen = (value: boolean): void => {
    setBenefitsConfigVersionEditModal(value);
  };

  return {
    benefitConfigVersion,
    benefitConfigVersionEditModalIsOpen,
    setSelectedBenefitsConfigVersion,
    clearSelectedBenefitsConfigVersion,
    setBenefitsConfigVersionEditModalIsOpen,
  };
};

const BenefitsConfigVersionContext = createContext<IBenefitsConfigVersionContext | null>(null);

export const BenefitsConfigVersionContextProvider: React.FC = ({ children }) => (
  <BenefitsConfigVersionContext.Provider value={useStore()}>{children}</BenefitsConfigVersionContext.Provider>
);

export const useBenefitsConfigVersion = (): IBenefitsConfigVersion | null =>
  useContext(BenefitsConfigVersionContext)?.benefitConfigVersion ?? null;

// eslint-disable-next-line no-unused-vars
export const useSetBenefitsConfigVersion = (): ((b: IBenefitsConfigVersion) => void) =>
  useContext(BenefitsConfigVersionContext)?.setSelectedBenefitsConfigVersion ?? (() => {});

export const useBenefitsConfigVersionEditModalIsOpen = (): boolean =>
  useContext(BenefitsConfigVersionContext)?.benefitConfigVersionEditModalIsOpen ?? false;

// eslint-disable-next-line no-unused-vars
export const useSetBenefitsConfigVersionEditModal = (): ((value: boolean) => void) =>
  useContext(BenefitsConfigVersionContext)?.setBenefitsConfigVersionEditModalIsOpen ?? (() => {});

export const useClearBenefitsConfigVersion = (): (() => void) =>
  useContext(BenefitsConfigVersionContext)?.clearSelectedBenefitsConfigVersion ?? (() => {});

const BenefitsConfigVersion: React.FC = () => {
  const { t } = useTranslation();
  const [columns, setColumns] = useState<TableColumn<IBenefitsConfigVersion>[]>(benefitsConfigVersionTableColumns);

  const { searchTerm, setSearchTerm, debouncedValue } = useDebounceSearchTerm(500);

  const { benefitType } = useParams();

  const setIsOpen = useSetBenefitsConfigVersionEditModal();
  const setConfigVersionToEdit = useSetBenefitsConfigVersion();

  const addNew = () => {
    setConfigVersionToEdit({
      type: benefitType as string,
      startDate: new Date(),
      endDate: new Date(),
      payrollStartDate: new Date(),
      payrollEndDate: new Date(),
      description: '',
      event: [],
    });
    setIsOpen(true);
  };
  return (
    <Container>
      <ItemContainerHeader>
        <HeaderLeftItems>
          <HeaderLeftFirstItem>
            <NavTextLink to="/benefits">
              <ArrowBack src={Arrow} />
            </NavTextLink>
            <PageTitle>{t('benefits.configVersion.title')}</PageTitle>
          </HeaderLeftFirstItem>
          <HeaderLeftSecondItem />
        </HeaderLeftItems>
        <HeaderRightItems>
          <SearchInput
            value={searchTerm}
            placeholder="Search..."
            handleChange={event => {
              setSearchTerm(event.target.value);
            }}
          />
          <RegularButton onClick={() => addNew()}>{t('global.form.add')}</RegularButton>
        </HeaderRightItems>
      </ItemContainerHeader>
      <ItemContainer>
        <TableSettingContainer>
          <SettingsButton columns={columns} setColumns={setColumns} />
        </TableSettingContainer>
        <>
          <BenefitsConfigVersionModal />
          <BenefitsConfigsVersionTable searchTerm={debouncedValue} columns={columns} type={benefitType as string} />
        </>
      </ItemContainer>
    </Container>
  );
};

export const BenefitsConfigVersionContextWrapper = () => (
  <BenefitsConfigVersionContextProvider>
    <BenefitsConfigVersion />
  </BenefitsConfigVersionContextProvider>
);
